import { CellContext, createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Case } from '../../../../shared/api/hscan/case';
import { genderToString } from '../../../../shared/utils';
import { ImageView } from '../../common/components/ImageView';
import {
  createTableElementInformationContext,
  createTableInformationContext,
  PresentationalTableProps,
} from '../../common/components/SortableTable/PresentationalTable';
import { useModalityLabel } from '../../hooks/modalityHook';

import { PreviewMobileImageTable } from './PreviewMobileImageTable';

export const { TableContext, TableInformationProvider } =
  createTableInformationContext<Case, 'caseId'>();

export const { TableElementContext, TableElementInformationProvider } =
  createTableElementInformationContext<Case, 'caseId'>(TableContext);

export const PreviewMobileCases = (
  props: Omit<PresentationalTableProps<Case, 'caseId'>, 'identifyKey'>,
) => {
  const { t: globalT } = useTranslation();
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.shareToPatientImageList.table',
  });
  const modalityLabel = useModalityLabel();

  const columnHelper = useMemo(() => createColumnHelper<Case>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.accessor('contentIds', {
        header: `${t('header.image')}`,
        cell: (context: unknown) => {
          const {
            getValue,
            width = '40px',
            height = '40px',
          } = context as CellContext<Case, string[]> & {
            width: string;
            height: string;
          };
          return (
            <ImageView
              width={width}
              height={height}
              imageId={getValue()?.[0] ?? ''}
            />
          );
        },
        meta: {
          cellStyle: {
            align: 'center',
            borderLeft: { style: 'solid' },
            borderRight: { style: 'solid' },
            isNonTextContent: true,
          },
          headerStyle: {
            align: 'center',
            borderLeft: { style: 'solid' },
            borderRight: { style: 'solid' },
          },
        },
      }),
      columnHelper.accessor('requestedDate', {
        header: `${t('header.requestedDate')}`,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? new Date(value).toLocaleDateString() : '';
        },
      }),
      columnHelper.accessor('acceptedDate', {
        header: `${t('header.acceptedDate')}`,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? new Date(value).toLocaleDateString() : '';
        },
      }),
      columnHelper.accessor('institutionName', {
        header: `${t('header.institutionName')}`,
      }),
      columnHelper.accessor('patientName', {
        header: `${t('header.patientName')}`,
      }),
      columnHelper.accessor('patientSex', {
        header: t('header.patientSex') as string,
        cell: ({ getValue }) =>
          globalT(`common.gender.${genderToString(getValue())}`),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('birthDate', {
        header: t('header.birthDate') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: t('header.studyDate') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('modality', {
        header: t('header.modality') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
        cell: ({ getValue }) => modalityLabel(getValue()),
      }),
      columnHelper.accessor('bodyPart', {
        header: t('header.bodyPart') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
        cell: ({ getValue }) => getValue()?.join(', '),
      }),
    ],
    [columnHelper, t, globalT, modalityLabel],
  );

  return (
    <TableInformationProvider
      {...props}
      cellOptions={cellOptions}
      identifyKey="caseId">
      <PreviewMobileImageTable />
    </TableInformationProvider>
  );
};
